import { CommunityPartner, CoreValue, CultureItem, LeadershipMember } from './types';

import ceoImage from '../assets/michael-stockman.jpg';
import isaac2 from '../assets/isaac2.jpg';
import Bizcoworkers from '../assets/Bizcoworkers.jpg';
import grantasImage from '../assets/Bizcoworkes2.jpg';
import michaelImage from '../assets/BIzcoworkers3.jpg';
import tammieImage from '../assets/Bizcoworkers4.jpg';
import natalieImage from '../assets/Bizcoworkers5.jpg';
import azFamilyPromise from '../assets/AZ-Greater_Phoenix_round_color-980x966.jpg';
import goodwill from '../assets/goodwillindustrylogo.svg';
import riseupwithservicenow from '../assets/riseup-promo-tile1.png';
import raspberryjam from '../assets/raspberryjam.jpg';

export const HERO = {
  title: 'Mission, Vision & Values',
  subtitle: 'Driving digital transformation with purpose and passion',
};

export const MISSION = {
  paragraphs: [
    'We are on a mission to transform the world of work by empowering employees and elevating customer experiences through innovative, value-driven solutions.',
    'We believe in enjoying the journey, listening first, collaborating openly, and delivering results that matter for our clients and our team.',
  ],
};

export const VISION = {
  paragraphs: [
    'To improve the quality of life for everyone we serve through meaningful work, strong relationships, and impactful technology. That means our clients, their teams and customers, our own employees, and the students we support.',
  ],
};

/**
 * The single canonical set of company values (Task 6). The homepage
 * previously carried a second, contradictory set (Innovation / Integrity /
 * Excellence / Partnership) -- that set has been retired in favor of this
 * one, which both the homepage and About page now render.
 */
export const CORE_VALUES: CoreValue[] = [
  {
    id: 1,
    iconKey: 'handshake',
    title: 'Customer Commitment',
    bullets: [
      'We listen, learn, and anticipate needs.',
      'We deliver with clarity, simplicity, and speed.',
      'We use technology as a force for good.',
    ],
  },
  {
    id: 2,
    iconKey: 'users',
    title: 'One Team',
    bullets: [
      'We support work-life balance and well-being.',
      'We build trust through transparency, respect, and honesty.',
      'We value ideas, collaboration, and shared success.',
      'We empower, grow, and reward passionate talent.',
    ],
  },
  {
    id: 3,
    iconKey: 'lightbulb',
    title: 'Quality & Innovation',
    bullets: [
      'We embrace continuous improvement and bold thinking.',
      'We make data-informed decisions with integrity.',
      'We learn quickly, adapt smartly, and act decisively.',
    ],
  },
  {
    id: 4,
    iconKey: 'globe',
    title: 'Community Orientation',
    bullets: [
      'We nurture partnerships and expand ecosystems locally and globally.',
      'We support workforce development through education and community initiatives.',
    ],
  },
];

// Leading/trailing whitespace in role strings ("Technical Lead ", " Software
// Engineer ") has been stripped per Task 1.
export const LEADERSHIP: LeadershipMember[] = [
  {
    name: 'Michael Stockman',
    role: 'Chief Executive Officer',
    bio: '40+ years experience in enterprise technology and digital transformation.',
    expertise: ['Strategy', 'Leadership', 'Digital Transformation'],
    linkedin: 'https://www.linkedin.com/in/mikerstockman/',
    image: ceoImage,
  },
  {
    name: 'Grantas Dapkus',
    role: 'Technical Lead',
    bio: 'Full-stack development expert leading our technical implementation teams.',
    expertise: ['Software Development', 'System Integration', 'Team Leadership'],
    linkedin: 'https://www.linkedin.com/in/gdapkus/',
    image: grantasImage,
  },
  {
    name: 'Michael Garrett',
    role: 'Software Engineer',
    bio: 'Machine learning specialist focused on delivering cutting-edge AI solutions.',
    expertise: ['Developer', 'Machine Learning', 'Data Science'],
    linkedin: 'https://www.linkedin.com/in/mgarrett2024/',
    image: michaelImage,
  },
  {
    name: 'Ariana Gonzalez',
    role: 'Project Manager and Business Analyst',
    bio: 'Specialized in enterprise consulting and business process optimization.',
    // "ServiceNow Partner" was removed from this list. Partner status is a
    // company-level relationship, not a personal skill, and rendering it as
    // one person's chip both misattributes it and buries it. DECISION FOR A
    // HUMAN: whether BizSolutions.Tech holds a current partner tier, and
    // where that belongs if so -- see PHASE2_NOTES.md.
    expertise: ['Process Design', 'Change Management'],
    linkedin: 'https://www.linkedin.com/in/arianagonzalezgg/',
    image: Bizcoworkers,
  },
  {
    name: 'Isaac Arzu',
    role: 'ServiceNow TA & Developer',
    bio: 'ServiceNow expert with extensive experience in platform implementation and optimization.',
    expertise: ['ServiceNow', 'Developer', 'Training'],
    linkedin: 'https://www.linkedin.com/in/isaac-arzu-56693633a/',
    image: isaac2,
  },
  {
    name: 'Natalie Feliciano',
    role: 'Junior Developer Intern',
    bio: 'Emerging developer focused on web modernization and API integrations.',
    expertise: ['React Development', 'API Integration', 'Web Design'],
    linkedin: 'https://www.linkedin.com/in/nkf-natalie-feliciano/',
    image: natalieImage,
  },
  {
    name: 'Tammie Hertel',
    role: 'Engagement Manager/Strategist/User Adoption and OCM Specialist',
    bio: '35+ years experience in enterprise technology implementation, user adoption of technology, and organizational change management.',
    expertise: ['Project Manager', 'Organizational Development', 'Instructional Design'],
    linkedin: 'https://www.linkedin.com/in/tammiehertel/',
    image: tammieImage,
  },
];

export const CULTURE_ITEMS: CultureItem[] = [
  {
    iconKey: 'users',
    title: 'Collaborative',
    description: 'We work together to deliver the best solutions.',
  },
  {
    iconKey: 'lightbulb',
    title: 'Innovative',
    description: 'Constantly exploring new technologies and approaches.',
  },
  {
    iconKey: 'globe',
    title: 'Community-Focused',
    description: 'Supporting education and workforce development initiatives.',
  },
];

export const STORY = {
  heading: 'The BizSolutions.Tech Story',
  paragraphs: [
    'We started BizSolutions.Tech in 2017 with the goal of creating a practice where we could enjoy creating technology solutions with and for our customers. We also wanted to make a difference - contribute to our communities, by sponsoring internships, conferences, educational events, and social change agencies.',
  ],
  internshipProgram: {
    title: 'Internship Program',
    body: "Our first interns from the University of Charleston, have been on fire, learning the ServiceNow platform. They have only gotten started. We can't wait to see what they accomplish next!",
  },
  // DECISION FOR A HUMAN (Task 6): this is the most prominent geographic
  // content on the page. Flagged in REMEDIATION_NOTES.md rather than removed
  // or rewritten.
  meetupSponsor: {
    title: 'ServiceNow Developer Meetup Sponsor',
    body: 'We co-host the Phoenix Arizona ServiceNow Developer Meetup, which is one of the most popular and well attended ServiceNow meetup in the world. We are in the process of building a Developer Meetup for Charleston, West Virginia and surrounding areas. If you are interested, drop us a line at info@bizsolutions.tech.',
  },
};

export const COMMUNITY_SECTION = {
  heading: 'Community Support',
  kicker: 'Giving back, not client references',
  intro: 'We support these organizations, which do so much for our communities and for those in need',
  badgeLabel: 'Nonprofit we support',
};

export const COMMUNITY_PARTNERS: CommunityPartner[] = [
  {
    name: 'Arizona Family Promise',
    url: 'https://familypromiseaz.org/',
    logo: azFamilyPromise,
    logoClassKey: 'familyPromise',
  },
  {
    name: 'Goodwill Industries',
    url: 'https://www.goodwill.org/',
    logo: goodwill,
    logoClassKey: 'goodwill',
  },
  {
    name: 'RiseUp with ServiceNow',
    url: 'https://www.servicenow.com/standard/other-documents/snu-riseup-with-servicenow.html',
    logo: riseupwithservicenow,
    logoClassKey: 'riseUp',
  },
  {
    name: 'Raspberry Jam',
    url: 'https://luma.com/sgkpdd5l?utm_source=chatgpt.com',
    logo: raspberryjam,
    logoClassKey: 'raspberryJam',
  },
];
